import {
  ANALYSIS_REASON_LABELS,
  type DifficultyLevel,
  type DomainCategory,
  type EmailThreadMetadata,
  type LabelCategoryMapping,
  type ThreadState,
  type TriageCategory,
} from '../types/email';

/**
 * Whether this thread carries a completed LLM analysis.
 *
 * Since ADR-0003 ingest and analysis are separate phases, so a thread can be
 * perfectly real and perfectly visible with every AI field null. Use this rather
 * than testing `synthesizedTitle` for truthiness — a `failed` thread and a
 * `pending` one look identical that way but mean different things to the user.
 */
export function isThreadAnalyzed(thread: EmailThreadMetadata): boolean {
  // Threads predating the analysisStatus column carry an analysis but no status.
  if (!thread.analysisStatus) return thread.synthesizedTitle != null;
  return thread.analysisStatus === 'analyzed';
}

/**
 * What to show as the thread's title, always non-empty.
 *
 * The AI title when there is one, the raw `Subject:` header when there isn't, and a
 * last-resort placeholder for a genuinely subject-less email. Every list, card and
 * header must route through this — a thread with no analysis rendering as a blank
 * row is the failure mode ADR-0003 exists to prevent.
 */
export function threadDisplayTitle(thread: EmailThreadMetadata): string {
  return thread.synthesizedTitle?.trim() || thread.subject?.trim() || '(no subject)';
}

/**
 * Short user-facing explanation for why a thread has no summary, or null when it
 * has one. Pair with a retry affordance where the reason is retryable — a blank
 * row that explains nothing teaches the user nothing.
 */
export function threadAnalysisNotice(thread: EmailThreadMetadata): string | null {
  if (isThreadAnalyzed(thread)) return null;
  if (thread.analysisStatus === 'pending' || !thread.analysisStatus) return 'Not analyzed yet';
  return thread.analysisReason ? ANALYSIS_REASON_LABELS[thread.analysisReason] : 'Not analyzed yet';
}

export type ThreadRecommendedActionKind =
  | 'needs-reply'
  | 'waiting-on-them'
  | 'deadline'
  | 'follow-up'
  | 'unblock'
  | 'decision'
  | 'approval'
  | 'meeting'
  | 'review';

export interface ThreadRecommendedAction {
  kind: ThreadRecommendedActionKind;
  label: string;
  detail: string | null;
  priority: number;
  thread: EmailThreadMetadata;
}

/**
 * Best currently-supported next action for one analyzed, unresolved email thread.
 *
 * "Needs reply" is emitted from the explicit `waiting_on_us` or `demands_reply` analysis tag;
 * `coreAsk` and `draftResponse` alone do not prove that the latest inbound question
 * remains unanswered.
 */
export function getThreadRecommendedAction(
  thread: EmailThreadMetadata
): ThreadRecommendedAction | null {
  if (thread.resolvedAt || !isThreadAnalyzed(thread)) return null;

  const deadline = thread.extractedEntities.deadlines[0] ?? null;
  if (thread.threadState === 'Imminent Deadline') {
    return {
      kind: 'deadline',
      label: 'Review imminent deadline',
      detail: deadline ?? thread.coreAsk,
      priority: 100,
      thread,
    };
  }
  if (
    thread.analysisTags?.includes('waiting_on_us') ||
    thread.analysisTags?.includes('demands_reply')
  ) {
    return {
      kind: 'needs-reply',
      label: 'Needs Our Reply',
      detail: thread.coreAsk,
      priority: 95,
      thread,
    };
  }
  if (
    thread.analysisTags?.includes('waiting_on_them') ||
    thread.threadState === 'Waiting on Them'
  ) {
    return {
      kind: 'waiting-on-them',
      label: 'Waiting on Their Reply',
      detail: thread.coreAsk,
      priority: 65,
      thread,
    };
  }
  if (thread.threadState === 'Chaser') {
    return {
      kind: 'follow-up',
      label: 'Respond to follow-up',
      detail: thread.coreAsk,
      priority: 90,
      thread,
    };
  }
  if (thread.analysisTags?.includes('asks_for_deadline')) {
    return {
      kind: 'deadline',
      label: 'Provide a deadline',
      detail: thread.coreAsk,
      priority: 85,
      thread,
    };
  }
  if (thread.threadState === 'Stalled') {
    return {
      kind: 'unblock',
      label: 'Unblock conversation',
      detail: thread.coreAsk,
      priority: 80,
      thread,
    };
  }
  if (deadline) {
    return {
      kind: 'deadline',
      label: 'Review deadline',
      detail: deadline,
      priority: 75,
      thread,
    };
  }
  if (thread.actionType === 'Requires Decision') {
    return {
      kind: 'decision',
      label: 'Make a decision',
      detail: thread.coreAsk,
      priority: 70,
      thread,
    };
  }
  if (thread.actionType === 'Quick Approval') {
    return {
      kind: 'approval',
      label: 'Review approval',
      detail: thread.coreAsk,
      priority: 60,
      thread,
    };
  }
  if (thread.actionType === 'Meeting') {
    return {
      kind: 'meeting',
      label: 'Handle meeting request',
      detail: thread.coreAsk,
      priority: 50,
      thread,
    };
  }
  if (thread.threadState === 'New' && thread.actionType !== 'FYI') {
    return {
      kind: 'review',
      label: 'Review request',
      detail: thread.coreAsk,
      priority: 40,
      thread,
    };
  }
  return null;
}

/** Highest-value email actions first, with newest mail breaking equal-priority ties. */
export function getRecommendedThreadActions(
  threads: EmailThreadMetadata[]
): ThreadRecommendedAction[] {
  return threads
    .map(getThreadRecommendedAction)
    .filter((action): action is ThreadRecommendedAction => action !== null)
    .sort(
      (a, b) => b.priority - a.priority || (b.thread.createdAt ?? 0) - (a.thread.createdAt ?? 0)
    );
}

export interface TriageFilters {
  domain: DomainCategory | 'all';
  difficulty: DifficultyLevel | 'all';
  state: ThreadState | 'all';
  label: string | 'all';
  hasDeadline: boolean;
  category: number | 'all';
  showResolved: boolean;
  sortBy: 'importance' | 'deadline' | 'newest' | 'effort';
  sortOrder: 'asc' | 'desc';
}

/**
 * `mappings` (and `labelWeights` below) may carry composite
 * `${accountEmail}::${providerLabelId}` keys from the resolved mail-tags
 * taxonomy (mail-tags redesign, tag-based settings cutover) alongside legacy
 * flat raw-label-id keys — composite wins when both exist, since it's the
 * disambiguated one. See `apps/clinic-portal/src/hooks/useClinic.ts`'s
 * `resolveTagWeightMap`/`resolveTagCategoryMappings`, which build the
 * composite entries this reads.
 */
function mappingMatchesLabel(
  mapping: LabelCategoryMapping,
  thread: EmailThreadMetadata,
  labelId: string
): boolean {
  return mapping.labelId === `${thread.accountEmail}::${labelId}` || mapping.labelId === labelId;
}

/**
 * The categories a single label rolls up to for this thread, checking the
 * composite `${accountEmail}::${labelId}` key before the legacy flat key. This
 * is the one place that rule lives — callers (`LabelBadgeList`) must not
 * reimplement the comparison, since a flat-only reimplementation silently
 * misses every mapping created through the mail-tags Settings UI.
 */
export function getCategoriesForLabel(
  thread: EmailThreadMetadata,
  mappings: LabelCategoryMapping[],
  categories: TriageCategory[],
  labelId: string
): TriageCategory[] {
  const categoryIds = new Set(
    mappings.filter((m) => mappingMatchesLabel(m, thread, labelId)).map((m) => m.categoryId)
  );
  return categories.filter((c) => categoryIds.has(c.id));
}

/** Same composite/flat-key precedence as `getCategoriesForLabel`, for display names. */
export function resolveLabelDisplayName(
  labelMap: Record<string, string>,
  thread: EmailThreadMetadata,
  labelId: string
): string {
  return labelMap[`${thread.accountEmail}::${labelId}`] || labelMap[labelId] || labelId;
}

/**
 * Where an unanalysed thread stands in each filter:
 *
 * `domain`, `difficulty` and `state` are AI-derived, so a thread with no analysis
 * matches *none* of their concrete values and drops out while such a filter is
 * active. That is deliberate and is the honest answer — asked for "Revenue"
 * threads, we must not hand back threads we have not classified. It does mean an
 * unanalysed thread is only reachable with those filters on 'all', which is why the
 * UI surfaces the "not analyzed" state prominently rather than relying on filters.
 *
 * `label`, `category`, `hasDeadline` and `showResolved` are unaffected: labels and
 * resolution come from the provider and the user, not the model, so they keep
 * working with AI switched off entirely.
 */
export function filterTriageThreads(
  threads: EmailThreadMetadata[],
  filters: Partial<TriageFilters>,
  mappings: LabelCategoryMapping[] = []
) {
  return threads.filter((t) => {
    if (!filters.showResolved && t.resolvedAt) return false;
    if (filters.domain && filters.domain !== 'all' && t.domainCategory !== filters.domain)
      return false;
    if (
      filters.difficulty &&
      filters.difficulty !== 'all' &&
      t.difficultyLevel !== filters.difficulty
    )
      return false;
    if (filters.state && filters.state !== 'all' && t.threadState !== filters.state) return false;
    if (filters.label && filters.label !== 'all' && !(t.labels || []).includes(filters.label))
      return false;
    if (filters.hasDeadline && t.extractedEntities.deadlines.length === 0) return false;
    if (filters.category && filters.category !== 'all') {
      const isInCategory = (t.labels || []).some((l) =>
        mappings.some((m) => mappingMatchesLabel(m, t, l) && m.categoryId === filters.category)
      );
      if (!isInCategory) return false;
    }
    return true;
  });
}

export function sortTriageThreads(
  threads: EmailThreadMetadata[],
  filters: Partial<TriageFilters>,
  labelWeights: Record<string, number> = {},
  domainWeights: Record<string, number> = {},
  categories: TriageCategory[] = [],
  mappings: LabelCategoryMapping[] = []
) {
  const sortBy = filters.sortBy || 'importance';
  const sortOrder = filters.sortOrder || 'desc';

  return [...threads].sort((a, b) => {
    let result = 0;

    if (sortBy === 'importance') {
      const aScore = calculateImportanceScore(a, labelWeights, domainWeights, categories, mappings);
      const bScore = calculateImportanceScore(b, labelWeights, domainWeights, categories, mappings);
      result = aScore - bScore;
    } else if (sortBy === 'deadline') {
      const aHas = a.extractedEntities.deadlines.length > 0;
      const bHas = b.extractedEntities.deadlines.length > 0;
      if (aHas && !bHas) {
        result = sortOrder === 'desc' ? 1 : -1;
      } else if (!aHas && bHas) {
        result = sortOrder === 'desc' ? -1 : 1;
      } else if (aHas && bHas) {
        const aDateVal = Date.parse(a.extractedEntities.deadlines[0]);
        const bDateVal = Date.parse(b.extractedEntities.deadlines[0]);
        const aTime = isNaN(aDateVal) ? Infinity : aDateVal;
        const bTime = isNaN(bDateVal) ? Infinity : bDateVal;
        result = aTime - bTime;
      } else {
        result = 0;
      }
    } else if (sortBy === 'effort') {
      // Unknown effort sinks to the bottom in *both* directions, the same way a
      // thread with no deadline does above. Coercing null to 0 would rank every
      // unanalysed thread as the quickest possible win, which is a worse lie than
      // admitting we don't know — so sortOrder is baked in here rather than left to
      // the flip at the end of this comparator.
      const aEffort = a.estimatedEffortMinutes;
      const bEffort = b.estimatedEffortMinutes;
      if (aEffort === null && bEffort === null) {
        result = 0;
      } else if (aEffort === null) {
        result = sortOrder === 'desc' ? -1 : 1;
      } else if (bEffort === null) {
        result = sortOrder === 'desc' ? 1 : -1;
      } else {
        result = aEffort - bEffort;
      }
    } else {
      result = (a.createdAt || 0) - (b.createdAt || 0);
    }

    if (result === 0) {
      if (sortBy !== 'importance') {
        const aScore = calculateImportanceScore(
          a,
          labelWeights,
          domainWeights,
          categories,
          mappings
        );
        const bScore = calculateImportanceScore(
          b,
          labelWeights,
          domainWeights,
          categories,
          mappings
        );
        if (aScore !== bScore) return sortOrder === 'desc' ? bScore - aScore : aScore - bScore;
      }
      result = a.threadId.localeCompare(b.threadId);
    }

    return sortOrder === 'desc' ? -result : result;
  });
}

export interface InboxGroup {
  accountEmail: string;
  label: string;
  threads: EmailThreadMetadata[];
}

const UNKNOWN_INBOX_KEY = '__unknown__';
const UNKNOWN_CUSTOMER_KEY = '__unknown__';

export function groupThreadsByInbox(threads: EmailThreadMetadata[]): InboxGroup[] {
  const groups = new Map<string, InboxGroup>();

  for (const thread of threads) {
    const key = thread.accountEmail || UNKNOWN_INBOX_KEY;
    let group = groups.get(key);
    if (!group) {
      group = {
        accountEmail: key,
        label: thread.accountEmail || 'Unknown Inbox',
        threads: [],
      };
      groups.set(key, group);
    }
    group.threads.push(thread);
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.accountEmail === UNKNOWN_INBOX_KEY) return 1;
    if (b.accountEmail === UNKNOWN_INBOX_KEY) return -1;
    return a.accountEmail.localeCompare(b.accountEmail);
  });
}

export function getThreadCustomerId(thread: EmailThreadMetadata): string {
  if (thread.contact) return `contact:${thread.contact.id}`;
  if (thread.senderEmail) return `email:${thread.senderEmail.toLowerCase()}`;
  return UNKNOWN_CUSTOMER_KEY;
}

export interface CustomerGroup {
  customerId: string;
  label: string;
  email?: string;
  threads: EmailThreadMetadata[];
}

export function groupThreadsByCustomer(threads: EmailThreadMetadata[]): CustomerGroup[] {
  const groups = new Map<string, CustomerGroup>();

  for (const thread of threads) {
    const customerId = getThreadCustomerId(thread);
    let group = groups.get(customerId);
    if (!group) {
      const label = thread.contact
        ? thread.contact.label
        : thread.senderEmail
          ? thread.senderName || thread.senderEmail
          : 'Unknown Sender';
      group = {
        customerId,
        label,
        email: thread.senderEmail,
        threads: [],
      };
      groups.set(customerId, group);
    }
    group.threads.push(thread);
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.customerId === UNKNOWN_CUSTOMER_KEY) return 1;
    if (b.customerId === UNKNOWN_CUSTOMER_KEY) return -1;
    return b.threads.length - a.threads.length;
  });
}

function calculateImportanceScore(
  thread: EmailThreadMetadata,
  labelWeights: Record<string, number>,
  domainWeights: Record<string, number>,
  categories: TriageCategory[],
  mappings: LabelCategoryMapping[]
): number {
  const labelWeight = (thread.labels || []).reduce(
    (max, l) => Math.max(max, labelWeights[`${thread.accountEmail}::${l}`] ?? labelWeights[l] ?? 0),
    0
  );
  // An unanalysed thread contributes no domain weight, but still scores on its
  // labels and their categories below — those come from the provider, not the
  // model, so importance ranking degrades rather than collapsing when AI is absent.
  const domainWeight = thread.domainCategory ? domainWeights[thread.domainCategory] || 0 : 0;
  const catWeight = (thread.labels || []).reduce((max, l) => {
    const catIds = mappings
      .filter((m) => mappingMatchesLabel(m, thread, l))
      .map((m) => m.categoryId);
    const weights = categories.filter((c) => catIds.includes(c.id)).map((c) => c.weight);
    return Math.max(max, ...weights, 0);
  }, 0);

  return labelWeight + domainWeight + catWeight;
}
